import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
  CleanTableExpandableRow,
  CleanTableExpandIcon,
  CleanTableExpandedContent,
  useExpandableTable,
} from "../ui/clean-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Plus, AlertTriangle, Shield, Edit, Trash2, User } from "lucide-react";

const RiskTable = ({
  risks,
  canManage,
  onEditRisk,
  onDeleteRisk,
  onCreateRisk,
}) => {
  // Use the expandable table hook
  const { isExpanded, toggleRow } = useExpandableTable();

  if (risks.length === 0) {
    return (
      <Card className="border-0 shadow-none">
        <CardContent>
          <div className="text-center py-12">
            <AlertTriangle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Risks Found</h3>
            <p className="text-muted-foreground mb-4">
              No risks have been created yet.
            </p>
            {canManage && onCreateRisk && (
              <Button
                size="sm"
                onClick={onCreateRisk}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Your First Risk
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-none">
      <CardContent>
        <TooltipProvider>
          <CleanTable>
            <CleanTableHeader>
              <CleanTableRow>
                <CleanTableHead className="w-8"></CleanTableHead>
                <CleanTableHead>Risk ID</CleanTableHead>
                <CleanTableHead>Risk Name</CleanTableHead>
                <CleanTableHead>Category</CleanTableHead>
                <CleanTableHead className="text-center">
                  Severity
                </CleanTableHead>
                <CleanTableHead className="text-center">
                  Likelihood
                </CleanTableHead>
                <CleanTableHead className="text-center">
                  Controls
                </CleanTableHead>
                {canManage && (
                  <CleanTableHead className="text-right">
                    Actions
                  </CleanTableHead>
                )}
              </CleanTableRow>
            </CleanTableHeader>
            <CleanTableBody>
              {risks.map((risk) => (
                <React.Fragment key={risk.id}>
                  {/* Main Risk Row */}
                  <CleanTableExpandableRow
                    isExpanded={isExpanded(risk.id)}
                    onToggle={(e) => {
                      // Prevent expansion when clicking action buttons
                      if (e.target.closest(".action-buttons")) {
                        e.stopPropagation();
                        return;
                      }
                      toggleRow(risk.id);
                    }}
                  >
                    <CleanTableExpandIcon isExpanded={isExpanded(risk.id)} />
                    <CleanTableCell>
                      <span className="text-sm font-mono text-gray-600">
                        {risk.custom_id || risk.id?.slice(0, 8)}
                      </span>
                    </CleanTableCell>
                    <CleanTableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {risk.name || "No name provided"}
                        </p>
                        {risk.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {risk.description}
                          </p>
                        )}
                      </div>
                    </CleanTableCell>
                    <CleanTableCell>
                      <span className="text-gray-900">
                        {risk.Category?.name || "Uncategorized"}
                      </span>
                    </CleanTableCell>
                    <CleanTableCell className="text-center">
                      <span className="text-gray-900 font-medium">
                        {risk.Severity?.value || "N/A"}
                      </span>
                    </CleanTableCell>
                    <CleanTableCell className="text-center">
                      <span className="text-gray-900 font-medium">
                        {risk.Likelihood?.value || "N/A"}
                      </span>
                    </CleanTableCell>
                    <CleanTableCell className="text-center">
                      <span className="text-sm font-medium">
                        {risk.Controls?.length || 0}
                      </span>
                    </CleanTableCell>
                    {canManage && (
                      <CleanTableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 action-buttons">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditRisk(risk);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit Risk</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteRisk(risk);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete Risk</TooltipContent>
                          </Tooltip>
                        </div>
                      </CleanTableCell>
                    )}
                  </CleanTableExpandableRow>

                  {/* Expanded Controls Content */}
                  {isExpanded(risk.id) && (
                    <CleanTableExpandedContent colSpan={canManage ? 8 : 7}>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            Associated Controls ({risk.Controls?.length || 0})
                          </h4>
                        </div>

                        {risk.description && (
                          <div className="bg-gray-50 p-3 rounded-md">
                            <h5 className="font-medium text-sm text-gray-900 mb-1">
                              Full Description
                            </h5>
                            <p className="text-sm text-gray-700">
                              {risk.description}
                            </p>
                          </div>
                        )}

                        {!risk.Controls || risk.Controls.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <Shield className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm">
                              No controls assigned to this risk
                            </p>
                          </div>
                        ) : (
                          <CleanTable>
                            <CleanTableHeader>
                              <CleanTableRow>
                                <CleanTableHead>Control ID</CleanTableHead>
                                <CleanTableHead>Name</CleanTableHead>
                                <CleanTableHead>Category</CleanTableHead>
                                <CleanTableHead>Sub-Category</CleanTableHead>
                                <CleanTableHead className="text-center">
                                  Assigned Users
                                </CleanTableHead>
                              </CleanTableRow>
                            </CleanTableHeader>
                            <CleanTableBody>
                              {risk.Controls.map((control) => (
                                <CleanTableRow key={control.id}>
                                  <CleanTableCell>
                                    <span className="text-sm font-mono text-gray-600">
                                      {control.custom_id ||
                                        control.id?.slice(0, 8)}
                                    </span>
                                  </CleanTableCell>
                                  <CleanTableCell>
                                    <span className="font-medium text-gray-900">
                                      {control.name || "No name provided"}
                                    </span>
                                  </CleanTableCell>
                                  <CleanTableCell>
                                    {control.SubCategory?.Category?.name ||
                                      control.Category?.name ||
                                      "Uncategorized"}
                                  </CleanTableCell>
                                  <CleanTableCell>
                                    {control.SubCategory?.name ||
                                      "No sub-category"}
                                  </CleanTableCell>
                                  <CleanTableCell className="text-center">
                                    {control.assigned_users &&
                                    control.assigned_users.length > 0 ? (
                                      <div className="flex items-center justify-center gap-1 text-sm text-gray-500">
                                        <User className="h-3 w-3" />
                                        <span>
                                          {control.assigned_users.length}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-500 text-sm">
                                        None
                                      </span>
                                    )}
                                  </CleanTableCell>
                                </CleanTableRow>
                              ))}
                            </CleanTableBody>
                          </CleanTable>
                        )}
                      </div>
                    </CleanTableExpandedContent>
                  )}
                </React.Fragment>
              ))}
            </CleanTableBody>
          </CleanTable>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

export default RiskTable;
